import { StyledChatConversations } from './style';
import { Button, Label, Table } from 'ui';
import { useIntl } from 'react-intl';
import { useState, useEffect } from 'react';
import { message, Modal } from 'antd';
import dayjs from 'dayjs';
import useQueryApiClient from 'utils/useQueryApiClient';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';
import { MobileListField } from 'ui/Card/MobileListCard';

export interface ConversationRow {
  id: string;
  other_farm_id: number;
  other_farm: { id: number; name: string; brand?: string } | null;
  subject: string | null;
  first_message_at: string;
  last_message_at: string;
  has_unread: boolean;
  status: 'new' | 'unread' | 'read';
}

interface ChatConversationsProps {
  reload: number;
  onConversationClick: (params: {
    conversationId: string;
    otherFarmId: number;
  }) => void;
}

export const ChatConversations = ({
  reload = 0,
  onConversationClick,
}: ChatConversationsProps) => {
  const intl = useIntl();
  const [reloadTrigger, setReloadTrigger] = useState(0);

  useEffect(() => {
    if (reload > 0) {
      setReloadTrigger((prev) => prev + 1);
    }
  }, [reload]);

  const { appendData: deleteConversation } = useQueryApiClient({
    request: {
      url: `/api/v2/messages/conversations/:conversationId`,
      method: 'DELETE',
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'chat.delete_success' }));
      setReloadTrigger((prev) => prev + 1);
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'messages.delete_error' }));
    },
  });

  const handleDelete = (record: ConversationRow) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'chat.delete_confirm' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => {
        deleteConversation(null, {
          conversationId: record.id,
        });
      },
    });
  };

  const getStatusLabel = (status: string, hasUnread: boolean) => {
    if (hasUnread) {
      return intl.formatMessage({ id: 'chat.status.unread' });
    }
    if (status === 'new') {
      return intl.formatMessage({ id: 'chat.status.new' });
    }
    return intl.formatMessage({ id: 'chat.status.read' });
  };

  const mobileItems: {
    coverFields: MobileListField[];
    bodyFields: MobileListField[];
    footerFields: MobileListField[];
  } = {
    coverFields: [
      {
        name: 'other_farm',
        render: (_: unknown, record: ConversationRow) =>
          record.other_farm?.name || record.other_farm?.brand || '-',
      },
    ],
    bodyFields: [
      {
        name: 'subject',
        label: intl.formatMessage({ id: 'chat.subject' }),
        render: (value: string) => value || '-',
      },
      {
        name: 'last_message_at',
        label: intl.formatMessage({ id: 'chat.last_message_date' }),
        render: (value: string) =>
          value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-',
      },
      {
        name: 'status',
        label: intl.formatMessage({ id: 'chat.status' }),
        render: (value: string, record: ConversationRow) => (
          <Label label={getStatusLabel(value, record.has_unread)} />
        ),
      },
    ],
    footerFields: [
      {
        name: 'open',
        render: (_: unknown, record: ConversationRow) => (
          <Button
            type="primary"
            size="small"
            label={intl.formatMessage({ id: 'chat.open_conversation' })}
            onClick={() =>
              onConversationClick({
                conversationId: record.id,
                otherFarmId: record.other_farm_id,
              })
            }
          />
        ),
      },
      {
        name: 'delete',
        render: (_: unknown, record: ConversationRow) => (
          <Button
            type="link"
            danger
            size="small"
            label={intl.formatMessage({ id: 'chat.delete_conversation' })}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleDelete(record);
            }}
          />
        ),
      },
    ],
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'chat.conversation_start_date' }),
      dataIndex: 'first_message_at',
      render: (value: string) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: intl.formatMessage({ id: 'chat.sender' }),
      dataIndex: ['other_farm', 'name'],
      render: (_: unknown, record: ConversationRow) =>
        record.other_farm?.name || record.other_farm?.brand || '-',
    },
    {
      title: intl.formatMessage({ id: 'chat.subject' }),
      dataIndex: 'subject',
      render: (value: string) => value || '-',
    },
    {
      title: intl.formatMessage({ id: 'chat.status' }),
      dataIndex: 'status',
      render: (value: string, record: ConversationRow) => (
        <Label label={getStatusLabel(value, record.has_unread)} />
      ),
    },
    {
      title: intl.formatMessage({ id: 'chat.last_message_date' }),
      dataIndex: 'last_message_at',
      render: (value: string) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: intl.formatMessage({ id: 'listings.actions' }),
      render: (record: ConversationRow) => (
        <Button
          type="link"
          danger
          label={intl.formatMessage({ id: 'chat.delete_conversation' })}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleDelete(record);
          }}
        />
      ),
    },
  ];

  return (
    <StyledChatConversations>
      <ResponsiveWrapper
        desktopComponent={
          <Table
            url="/api/v2/messages/conversations"
            columns={columns}
            reload={reloadTrigger}
            rowClassName={(record) =>
              `is-clickable ${(record as ConversationRow)?.has_unread ? 'is-unread' : ''}`
            }
            onRow={(record: ConversationRow) => ({
              onClick: () =>
                onConversationClick({
                  conversationId: record.id,
                  otherFarmId: record.other_farm_id,
                }),
              style: { cursor: 'pointer' },
            })}
          />
        }
        mobileComponent={
          <MobileList
            url="/api/v2/messages/conversations"
            filter={{}}
            reload={reloadTrigger}
            rowKey="id"
            coverFields={mobileItems.coverFields}
            bodyFields={mobileItems.bodyFields}
            footerFields={mobileItems.footerFields}
          />
        }
      />
    </StyledChatConversations>
  );
};
